import { HttpErrorResponse } from "@angular/common/http";

import { ExtendedGfhAccount } from "@core/models/extended-gfh-account.model";
import { PendingIban } from "@core/models/pending-iban.model";
import { PendingNav } from "@core/models/pending-nav.model";
import { ProductVersions } from "@core/models/product-versions.model";
import { TPagination } from "@custom-types/sorting";
import * as DashboardActions from "@features/dashboard/store/dashboard.actions";
import { createReducer, on } from "@ngrx/store";

export interface DashboardState {
  pendingNavList: Array<PendingNav>;
  productVersionList: Array<ExtendedGfhAccount>;
  selfApprovalList: Array<PendingIban>;
  approvalList: Array<PendingIban>;
  lastSevenDaysList: Array<PendingIban>;
  productVersions: ProductVersions;
  productVersionLength: number;
  loading: boolean;
  selfApprovalLoading: boolean;
  approvalLoading: boolean;
  lastSevenDaysLoading: boolean;
  error: HttpErrorResponse | null;
  productVersionPagination: Partial<TPagination> | undefined;
}

const initialState: DashboardState = {
  pendingNavList: [],
  productVersionList: [],
  selfApprovalList: [],
  approvalList: [],
  lastSevenDaysList: [],
  productVersions: new ProductVersions(),
  productVersionLength: 0,
  loading: false,
  selfApprovalLoading: false,
  approvalLoading: false,
  lastSevenDaysLoading: false,
  error: null,
  productVersionPagination: undefined,
};

export const DASHBOARD_REDUCER = createReducer(
  initialState,
  on(
    DashboardActions.DASHBOARD_ACTION_FAILURE,
    (state, action): DashboardState => {
      return {
        ...state,
        error: action.error,
      };
    }
  ),
  on(
    DashboardActions.SET_DASHBOARD_LOADING,
    (state: DashboardState, action: { loading: boolean }): DashboardState => {
      return {
        ...state,
        loading: action.loading,
      };
    }
  ),
  on(
    DashboardActions.SET_PENDING_NAV,
    (
      state: DashboardState,
      action: {
        pendingNavList: Array<PendingNav>;
      }
    ): DashboardState => {
      return {
        ...state,
        pendingNavList: action.pendingNavList,
      };
    }
  ),
  on(
    DashboardActions.SET_PRODUCT_VERSION_CHANGED_LIST,
    (
      state: DashboardState,
      action: {
        productVersionList: Array<ExtendedGfhAccount>;
      }
    ): DashboardState => {
      return {
        ...state,
        productVersionList: action.productVersionList,
      };
    }
  ),
  on(
    DashboardActions.SET_SELF_APPROVAL_TABLE,
    (
      state: DashboardState,
      action: {
        selfApprovalList: Array<PendingIban>;
      }
    ): DashboardState => {
      return {
        ...state,
        selfApprovalList: action.selfApprovalList,
      };
    }
  ),
  on(
    DashboardActions.SET_SELF_APPROVAL_TABLE_LOADING,
    (
      state: DashboardState,
      action: {
        selfApprovalLoading: boolean;
      }
    ): DashboardState => {
      return {
        ...state,
        selfApprovalLoading: action.selfApprovalLoading,
      };
    }
  ),
  on(
    DashboardActions.SET_APPROVAL_TABLE,
    (
      state: DashboardState,
      action: {
        approvalList: Array<PendingIban>;
      }
    ): DashboardState => {
      return {
        ...state,
        approvalList: action.approvalList,
      };
    }
  ),
  on(
    DashboardActions.SET_APPROVAL_TABLE_LOADING,
    (
      state: DashboardState,
      action: {
        approvalLoading: boolean;
      }
    ): DashboardState => {
      return {
        ...state,
        approvalLoading: action.approvalLoading,
      };
    }
  ),
  on(
    DashboardActions.SET_LAST_SEVEN_DAYS_APPROVED_TABLE,
    (
      state: DashboardState,
      action: {
        lastSevenDaysList: Array<PendingIban>;
      }
    ): DashboardState => {
      return {
        ...state,
        lastSevenDaysList: action.lastSevenDaysList,
      };
    }
  ),
  on(
    DashboardActions.SET_LAST_SEVEN_DAYS_APPROVED_TABLE_LOADING,
    (
      state: DashboardState,
      action: {
        lastSevenDaysLoading: boolean;
      }
    ): DashboardState => {
      return {
        ...state,
        lastSevenDaysLoading: action.lastSevenDaysLoading,
      };
    }
  ),
  on(
    DashboardActions.SET_PRODUCT_VERSIONS,
    (
      state: DashboardState,
      action: {
        productVersions: ProductVersions;
      }
    ): DashboardState => {
      return {
        ...state,
        productVersions: action.productVersions,
      };
    }
  ),
  on(
    DashboardActions.SET_PRODUCT_VERSION_CHANGED_LENGTH,
    (
      state: DashboardState,
      action: {
        length: number;
      }
    ): DashboardState => {
      return {
        ...state,
        productVersionLength: action.length,
      };
    }
  ),
  on(
    DashboardActions.SET_PRODUCT_VERSION_CHANGED_PAGINATION,
    (
      state: DashboardState,
      action: {
        pagination: Partial<TPagination> | undefined;
      }
    ): DashboardState => {
      return {
        ...state,
        productVersionPagination: action.pagination,
      };
    }
  )
);
